import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { logoutAndRedirect } from "@/lib/logout";
import { Loader2 } from "lucide-react";
import { PortalPage, PortalHero, InlineError, GoldButton, UnauthorizedScreen } from "./shared";
import { usePageTitle } from "@/hooks/use-page-title";
import { DnaLogo } from "@/components/brand/DnaLogo";

export default function PortalLoginPage() {
  usePageTitle("Portal Sign In — DNA");
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showDenied, setShowDenied] = useState(false);

  // Redirect already-authenticated users based on their role
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/auth/user", { credentials: "include" });
        if (!res.ok) return; // Not authenticated — stay on login
        const user = await res.json();
        if (!user) return;
        const role = user.role as string;
        if (role === "service_bureau") {
          setLocation("/portal/request");
        } else if (role === "admin" || role === "staff") {
          window.location.href = "/";
        } else if (role === "direct_client") {
          // direct_client is not allowed in the portal — show 403 screen
          setShowDenied(true);
        }
      } catch (err) {
        console.warn("[PortalLogin] Auth check on mount failed:", err);
      }
    })();
  }, []);

  const params = new URLSearchParams(window.location.search);
  const expired = params.get("expired") === "1";

  const loginMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/local", { email, password });
      return res.json();
    },
    onSuccess: async () => {
      try {
        const userRes = await fetch("/api/auth/user", { credentials: "include" });
        if (!userRes.ok) {
          setError("Login failed. Please try again.");
          return;
        }
        const user = await userRes.json();
        if (!user) {
          setError("Login failed. Please try again.");
          return;
        }

        const role = user.role as string;

        // direct_client cannot use the portal — show the 403 restriction screen
        if (role === "direct_client") {
          setShowDenied(true);
          return;
        }

        // admin / staff → redirect to main app, not portal
        if (role === "admin" || role === "staff") {
          window.location.href = "/";
          return;
        }

        if (role === "service_bureau") {
          window.location.href = "/portal/request";
          return;
        }

        setError("Your account does not have access to this portal.");
      } catch (err) {
        console.warn("Portal login error", err);
        setError("Login failed. Please try again.");
      }
    },
    onError: (err: Error) => {
      const msg = err.message ?? "";
      if (
        msg.includes("403") ||
        msg.includes("deactivated") ||
        msg.includes("inactive")
      ) {
        setError("Your account is inactive. Please contact support.");
      } else if (msg.includes("401") || msg.includes("400")) {
        setError("Incorrect email or password.");
      } else {
        setError("Login failed. Please try again.");
      }
    },
  });

  const handleSubmit = () => {
    setError(null);
    loginMutation.mutate();
  };

  if (showDenied) {
    const handleDeniedLogout = async () => {
      try {
        await logoutAndRedirect("/portal/login");
      } finally {
        setShowDenied(false);
        setEmail("");
        setPassword("");
      }
    };
    return <UnauthorizedScreen onLogout={handleDeniedLogout} />;
  }

  return (
    <PortalPage>
      <PortalHero
        eyebrow="Don Norman Associates"
        title={<>Member<br />Portal</>}
        subtitle="Secure access for authorized members."
      />

      <div
        style={{
          padding: "0 24px 64px",
          maxWidth: 440,
          margin: "-48px auto 0",
          position: "relative",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(27,43,75,0.18)",
            padding: "40px 48px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ margin: "0 auto 16px", display: "flex", justifyContent: "center" }}>
              <DnaLogo height={40} style={{ margin: "0 auto" }} />
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 22,
                fontWeight: 700,
                color: "#1B2B4B",
                marginBottom: 4,
              }}
            >
              Member Portal
            </h2>
            <p style={{ fontSize: 14, color: "#6b7280" }}>
              Sign in to manage authority letter requests.
            </p>
          </div>

          {expired && !error && (
            <InlineError message="Your session has expired. Please sign in again." />
          )}

          {error && <InlineError message={error} />}

          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#1B2B4B",
                display: "block",
                marginBottom: 6,
              }}
            >
              Email Address <span style={{ color: "#C9962A" }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={{
                width: "100%",
                padding: "13px 16px",
                fontSize: 15,
                color: "#1a1a1a",
                background: "white",
                border: "1.5px solid #e2ddd6",
                borderRadius: 10,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#1B2B4B",
                display: "block",
                marginBottom: 6,
              }}
            >
              Password <span style={{ color: "#C9962A" }}>*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={{
                width: "100%",
                padding: "13px 16px",
                fontSize: 15,
                color: "#1a1a1a",
                background: "white",
                border: "1.5px solid #e2ddd6",
                borderRadius: 10,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <GoldButton
            onClick={handleSubmit}
            disabled={loginMutation.isPending || !email || !password}
          >
            {loginMutation.isPending && (
              <Loader2 size={18} style={{ animation: "spin 0.8s linear infinite" }} />
            )}
            {loginMutation.isPending ? "Signing in…" : "Sign In"}
          </GoldButton>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <a
              href="/forgot-password?context=portal"
              style={{
                fontSize: 13,
                color: "#C9962A",
                textDecoration: "none",
              }}
              onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              Forgot your password?
            </a>
          </div>
        </div>
      </div>
    </PortalPage>
  );
}
